import React from "react";

/**
 * Renders a JSON-LD structured data block. React 19 hoists <script> tags into <head>
 * automatically. JSON-LD requires inline <script> content, so dangerouslySetInnerHTML
 * is the standard approach (recommended by Google + schema.org). To make it XSS-safe
 * we escape every character that could break out of the <script> tag or the JS
 * parser: `<`, `>`, `&`, and the unicode line-separator code points U+2028/U+2029.
 */
const escapeForScript = (json) =>
    json
        .replace(/&/g, "\\u0026")
        .replace(/</g, "\\u003c")
        .replace(/>/g, "\\u003e")
        .replace(/\u2028/g, "\\u2028")
        .replace(/\u2029/g, "\\u2029");

export const JsonLd = ({ data }) => {
    if (!data) return null;
    const json = escapeForScript(JSON.stringify(data));
    return (
        <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: json }}
        />
    );
};

const SITE = {
    name: "SAVNT",
    url: "https://savnt242.com",
    logo: "https://savnt242.com/savnt-logo.png",
};

export const organizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE.url,
    logo: SITE.logo,
    description:
        "SAVNT — For The Chosen. Timeless streetwear founded in Nassau, Bahamas in 2026. Designed in Nassau, manufactured in China and Japan, drawn from international influences.",
    foundingDate: "2026",
    foundingLocation: "Nassau, Bahamas",
    sameAs: [
        "https://www.instagram.com/savnt",
        "https://twitter.com/savnt",
        "https://www.youtube.com/@savnt",
    ],
});

export const websiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.url,
    potentialAction: {
        "@type": "SearchAction",
        target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE.url}/shop?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
    },
});

export const productSchema = ({ product, reviews = [] }) => {
    const inStock = Object.values(product.stock || {}).some((c) => c > 0);
    const verified = reviews.filter((r) => r.verified);
    const ratings = verified.map((r) => r.rating);
    const aggregate =
        ratings.length > 0
            ? {
                  "@type": "AggregateRating",
                  ratingValue: (ratings.reduce((s, n) => s + n, 0) / ratings.length).toFixed(1),
                  reviewCount: ratings.length,
                  bestRating: "5",
                  worstRating: "1",
              }
            : undefined;

    return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: product.description,
        image: product.images,
        sku: product.id,
        category: product.category,
        material: product.fabric,
        brand: {
            "@type": "Brand",
            name: SITE.name,
        },
        offers: {
            "@type": "Offer",
            url: `${SITE.url}/product/${product.slug}`,
            priceCurrency: "USD",
            price: product.price.toFixed(2),
            availability: inStock
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            itemCondition: "https://schema.org/NewCondition",
        },
        ...(aggregate && { aggregateRating: aggregate }),
        review: verified.slice(0, 10).map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name },
            reviewRating: {
                "@type": "Rating",
                ratingValue: String(r.rating),
                bestRating: "5",
            },
            name: r.title,
            reviewBody: r.body,
            datePublished:
                typeof r.created_at === "string"
                    ? r.created_at.slice(0, 10)
                    : new Date(r.created_at).toISOString().slice(0, 10),
        })),
    };
};

export const articleSchema = (entry) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: entry.title,
    description: entry.excerpt,
    image: entry.cover,
    author: { "@type": "Organization", name: SITE.name },
    publisher: {
        "@type": "Organization",
        name: SITE.name,
        logo: { "@type": "ImageObject", url: SITE.logo },
    },
    articleSection: entry.chapter,
    datePublished: entry.date,
    mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${SITE.url}/journal/${entry.slug}`,
    },
});

export const localBusinessSchema = (store) => ({
    "@context": "https://schema.org",
    "@type": store.flagship ? "ClothingStore" : "Store",
    name: store.name,
    address: {
        "@type": "PostalAddress",
        streetAddress: store.address,
        addressLocality: store.city,
        addressCountry: store.country,
    },
    telephone: store.phone,
    openingHours: store.hours,
    parentOrganization: { "@type": "Organization", name: SITE.name },
});
